import {
  CaptionedImage,
  DetailsPanel,
  GenderIcon,
} from '@/components/controls';
import { useNichePersonImage } from '@/hooks';
import { toProperCase } from '@/utils';
import {
  Map as MapIcon,
  SportsKabaddi as SportsKabaddiIcon,
  VideoCameraFront as VideoCameraFrontIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { differenceInYears, format } from 'date-fns';

function Subject({ subject }) {
  const {
    query: { data: imageSrc, isFetching },
  } = useNichePersonImage(subject?.id);

  if (isFetching) {
    return <Skeleton variant="rounded" sx={{ minWidth: 200 }} height={240} />;
  }

  return (
    <CaptionedImage
      imageSrc={imageSrc}
      height={240}
      fallbackWidth={200}
      topCaption={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Box>
            {toProperCase(
              [subject?.forenames, subject?.surname].filter(Boolean).join(' '),
            )}
          </Box>
          <GenderIcon gender={subject?.gender} />
        </Stack>
      }
      bottomCaption={
        subject?.dateOfBirth && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Typography>{format(subject.dateOfBirth, 'dd/MM/yyyy')}</Typography>
            <Typography sx={{ color: 'primary.main' }}>
              ({differenceInYears(new Date(), subject.dateOfBirth)})
            </Typography>
          </Stack>
        )
      }
    />
  );
}

export function CrimeAgainstStaff({ crime, onShowMapClick }) {
  const { occurrence, aggrievedOfficer, subjects = [] } = crime;

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={
          <Avatar>
            <SportsKabaddiIcon />
          </Avatar>
        }
        action={
          occurrence?.address && (
            <Tooltip title="Show map">
              <IconButton size="small" onClick={onShowMapClick}>
                <MapIcon sx={{ fontSize: 'inherit' }} />
              </IconButton>
            </Tooltip>
          )
        }
        title={occurrence?.number}
        subheader={
          occurrence?.reportedTime &&
          format(occurrence.reportedTime, 'dd/MM/yyyy HH:mm')
        }
      />
      {subjects.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ overflowX: 'auto', px: 1, pb: 1 }}
        >
          {subjects.map((subject) => (
            <Subject key={subject.id} subject={subject} />
          ))}
        </Stack>
      )}
      <Stack spacing={1} sx={{ p: 1 }}>
        {occurrence?.summary && (
          <Typography variant="body2" sx={{ p: 1 }}>
            {occurrence.summary}
          </Typography>
        )}{' '}
        <DetailsPanel
          title="Officer Involved"
          entries={[
            {
              label: 'Name',
              value: [aggrievedOfficer?.forenames, aggrievedOfficer?.surname]
                .filter(Boolean)
                .join(' '),
            },
            {
              label: 'Collar Number',
              value: aggrievedOfficer?.collarNumber,
            },
          ]}
        />
        <DetailsPanel
          title="Arrest"
          entries={[
            {
              label: 'Offence',
              value: occurrence?.offence,
            },
            {
              label: 'Outcome',
              value: occurrence?.outcome,
            },
          ]}
        />
        <DetailsPanel
          title="Owned by"
          entries={[
            {
              label: 'OIC',
              value: occurrence?.officerInCase?.collarNumber,
            },
            {
              label: 'Team',
              value: occurrence?.officerInCase?.team,
            },
          ]}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ p: 1, justifyContent: 'flex-end' }}
        >
          <Tooltip
            title={
              occurrence?.bodyWornVideo?.recordedOnDEMS
                ? 'Recorded on DEMS'
                : 'Not recorded on DEMS'
            }
            placement="top-end"
            arrow
          >
            <VideoCameraFrontIcon
              color={
                occurrence?.bodyWornVideo?.recordedOnDEMS ? 'success' : 'error'
              }
              sx={{ fontSize: 16 }}
            />
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}
